/**
 * AP Invoices Demo — "make me an AP processing workflow" graph.
 *
 * THROWAWAY DEMO CODE. Models the real AP invoice ingest pipeline as a ReactFlow
 * graph rendered with the PRODUCT'S real `StepNode`. Node `type`/`name`/`category`
 * and the per-node sockets match the actual step configs (Split Document (V2),
 * Extract Document Content (V2), Extract AP Invoice from Document, Create AP
 * Invoice Draft, Match Invoice Line Items, Schedule Payment, Confidence
 * Threshold), so the canvas reads as the genuine editor. The Famous fetch nodes
 * (Fetch PO/SO/TO, Ingest Sales Load) are intentionally omitted. The
 * Confidence-Threshold gate is `deferred` and only enters the graph once the
 * user asks to auto-accept high-confidence invoices. Delete with src/demo/ap.
 */

import type { Edge, Node } from '@xyflow/react';
import { IOType } from '@erp/shared-types/node-graph';

export const AP_WORKFLOW_NAME = 'AP Invoice Processing';
export const AP_WORKFLOW_DESCRIPTION =
  'Ingest invoice emails, split and OCR the documents, extract and create a draft invoice, match line items, and schedule payment — auto-accepting high-confidence matches.';

interface ApStepSpec {
  id: string;
  /** Real step type slug — drives StepNode identity. */
  stepType: string;
  /** Real step name (node title). */
  name: string;
  /** Real step category — drives the StepNode header colour. */
  category: string;
  position: { x: number; y: number };
  inputs: string[];
  outputs: string[];
  /** Held back until the user asks to auto-accept (the confidence gate). */
  deferred?: boolean;
}

const AP_STEPS: ApStepSpec[] = [
  {
    id: 'ap-wf-trigger',
    stepType: 'event_trigger',
    name: 'Event Trigger',
    category: 'Trigger',
    position: { x: 0, y: 260 },
    inputs: [],
    outputs: [
      'Email Rule Option Id',
      'Email Rule Id',
      'Email Rule Ids',
      'Inbound Email',
      'Documents',
    ],
  },
  {
    id: 'ap-wf-split',
    stepType: 'split_document_v2',
    name: 'Split Document (V2)',
    category: 'AI & Agents',
    position: { x: 380, y: 0 },
    inputs: ['Document'],
    outputs: [
      'Ap Invoice',
      'Ap Invoice Update',
      'Carrier Confirmation',
      'Bill Of Lading',
      'Proof Of Delivery',
      'Receiving Stamp',
      'Receipt',
      'All Documents',
    ],
  },
  {
    id: 'ap-wf-extract-content',
    stepType: 'extract_document_content_v2',
    name: 'Extract Document Content (V2)',
    category: 'AI & Agents',
    position: { x: 380, y: 470 },
    inputs: [
      'Inbound Email',
      'Extraction Config',
      'Origin Type',
      'Conversation Id',
    ],
    outputs: [
      'Inbound Email',
      'Origin Type',
      'Conversation Id',
      'Documents',
      'Document Ocr Results',
      'Processed Count',
      'Failed Count',
      'Skipped Count',
    ],
  },
  {
    id: 'ap-wf-extract-invoice',
    stepType: 'extract_ap_invoice_from_document',
    name: 'Extract AP Invoice from Document',
    category: 'Data Access',
    position: { x: 800, y: 250 },
    inputs: ['Document', 'Inbound Email'],
    outputs: ['Extraction Result Id', 'Extraction Result'],
  },
  {
    id: 'ap-wf-create-draft',
    stepType: 'create_ap_invoice_draft',
    name: 'Create AP Invoice Draft',
    category: 'Data Access',
    position: { x: 1180, y: 250 },
    inputs: ['Extraction Result Id'],
    outputs: ['Ap Invoice', 'Invoice Number', 'Reference Ids'],
  },
  {
    id: 'ap-wf-schedule',
    stepType: 'schedule_payment',
    name: 'Schedule Payment',
    category: 'Utility',
    position: { x: 1560, y: 30 },
    inputs: ['Ap Invoice'],
    outputs: [],
  },
  {
    id: 'ap-wf-match',
    stepType: 'match_invoice_line_items',
    name: 'Match Invoice Line Items',
    category: 'Transformation',
    position: { x: 1560, y: 290 },
    inputs: [
      'Ap Invoice',
      'Purchase Order',
      'Purchase Orders',
      'Sales Orders',
      'Transfer Orders',
    ],
    outputs: [
      'Lowest Confidence',
      'Famous Status Not Ready',
      'Unready References',
    ],
  },
  {
    id: 'ap-wf-confidence',
    stepType: 'confidence_threshold',
    name: 'Confidence Threshold',
    category: 'Control Flow',
    position: { x: 1960, y: 290 },
    inputs: ['Confidence'],
    outputs: ['Threshold Met', 'Threshold Not Met'],
    deferred: true,
  },
];

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function socketHandleId(
  nodeId: string,
  io: 'in' | 'out',
  name: string,
): string {
  return `${nodeId}-${io}-${slug(name)}`;
}

function toSocketMap(
  nodeId: string,
  names: string[],
  io: 'in' | 'out',
): Record<string, { id: string; ioType: IOType; socketType: string }> {
  const ioType = io === 'in' ? IOType.INPUT : IOType.OUTPUT;
  const out: Record<
    string,
    { id: string; ioType: IOType; socketType: string }
  > = {};
  for (const name of names) {
    // Key is the display name; id is a stable slug edges reference.
    out[name] = {
      id: socketHandleId(nodeId, io, name),
      ioType,
      socketType: 'json',
    };
  }
  return out;
}

// Data-socket connections (source output → target input). The confidence edge
// is added with the gate. Famous fetch nodes are omitted, so Match's PO/SO/TO
// inputs stay unconnected (as they would with the Famous steps removed).
interface ApEdgeSpec {
  source: string;
  sourceSocket: string;
  target: string;
  targetSocket: string;
}

const AP_EDGES: ApEdgeSpec[] = [
  {
    source: 'ap-wf-trigger',
    sourceSocket: 'Documents',
    target: 'ap-wf-split',
    targetSocket: 'Document',
  },
  {
    source: 'ap-wf-trigger',
    sourceSocket: 'Inbound Email',
    target: 'ap-wf-extract-content',
    targetSocket: 'Inbound Email',
  },
  {
    source: 'ap-wf-split',
    sourceSocket: 'All Documents',
    target: 'ap-wf-extract-invoice',
    targetSocket: 'Document',
  },
  {
    source: 'ap-wf-extract-content',
    sourceSocket: 'Inbound Email',
    target: 'ap-wf-extract-invoice',
    targetSocket: 'Inbound Email',
  },
  {
    source: 'ap-wf-extract-invoice',
    sourceSocket: 'Extraction Result Id',
    target: 'ap-wf-create-draft',
    targetSocket: 'Extraction Result Id',
  },
  {
    source: 'ap-wf-create-draft',
    sourceSocket: 'Ap Invoice',
    target: 'ap-wf-match',
    targetSocket: 'Ap Invoice',
  },
  {
    source: 'ap-wf-create-draft',
    sourceSocket: 'Ap Invoice',
    target: 'ap-wf-schedule',
    targetSocket: 'Ap Invoice',
  },
  {
    source: 'ap-wf-match',
    sourceSocket: 'Lowest Confidence',
    target: 'ap-wf-confidence',
    targetSocket: 'Confidence',
  },
];

/**
 * Build the ReactFlow nodes/edges for the current build state. The confidence
 * gate (and its incoming edge) is hidden until `confidenceAdded`; when shown it
 * carries the product's real "added" draft treatment.
 */
export function buildApWorkflowGraph(confidenceAdded: boolean): {
  nodes: Node[];
  edges: Edge[];
} {
  const steps = AP_STEPS.filter((s) => !s.deferred || confidenceAdded);
  const nodes: Node[] = steps.map((s) => ({
    id: s.id,
    type: 'stepNode',
    position: s.position,
    draggable: true,
    data: {
      label: s.name,
      category: s.category,
      stepType: s.stepType,
      editable: false,
      inputs: toSocketMap(s.id, s.inputs, 'in'),
      outputs: toSocketMap(s.id, s.outputs, 'out'),
      ...(s.deferred ? { __draftState: 'added' as const } : {}),
    },
  }));

  const visible = new Set(steps.map((s) => s.id));
  const edges: Edge[] = AP_EDGES.filter(
    (e) => visible.has(e.source) && visible.has(e.target),
  ).map((e) => {
    const added = e.target === 'ap-wf-confidence';
    return {
      id: `e-${e.source}-${slug(e.sourceSocket)}-${e.target}`,
      source: e.source,
      sourceHandle: socketHandleId(e.source, 'out', e.sourceSocket),
      target: e.target,
      targetHandle: socketHandleId(e.target, 'in', e.targetSocket),
      animated: true,
      style: added
        ? { stroke: '#8b5cf6', strokeWidth: 2 }
        : { stroke: '#94a3b8', strokeWidth: 2 },
    };
  });

  return { nodes, edges };
}
